import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Alert, Anchor, Button, Tabs, TextField } from '@monash/portal-react';
import { AppIcons, deepClone } from '@monash/portal-frontend-common';
import { getShortcut } from '../../utils';
import ShortcutIconButton from '@/components/pages/custom/components/shortcut-icon-button/ShortcutIconButton';
import emojis from '@/components/pages/custom/components/icon-input/icons-menu/emoji.json';
import Shortcut from '../../shortcut/Shortcut';
import c from './edit-shortcut-mobile-form.module.scss';
import { nanoid } from 'nanoid';

export const EditShortcutMobileForm = ({
  originalShortcut,
  setInEditShortcuts,
  isConfirmingBackButton,
  closeEditForm,
  setIsListDirty,
  setIsFormDirty,
  isFormDirty,
}) => {
  // Local state for the form
  const [localShortcut, setLocalShortcut] = useState(
    deepClone(originalShortcut) || { name: '', url: '', icon: null }
  );
  const [isNameValid, setIsNameValid] = useState(
    originalShortcut?.name || null
  );
  const [isUrlValid, setIsUrlValid] = useState(originalShortcut?.url || null);

  const [selectedTab, setSelectedTab] = React.useState(0);
  const TAB_NAMES = ['Favicon', 'Emojis', 'Icons'];

  // Initialize local state when originalShortcut changes
  useEffect(() => {
    if (originalShortcut) {
      setLocalShortcut(deepClone(originalShortcut));
    }
  }, [originalShortcut?.id]);

  // Check if current form is valid
  const isFormValid = localShortcut.name && localShortcut.url;

  // Update local state
  const updateLocalShortcut = (value, key) => {
    if (!isFormDirty) {
      setIsFormDirty(true);
    }
    setLocalShortcut((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Apply changes to parent state
  const applyChanges = () => {
    if (originalShortcut) {
      setInEditShortcuts((inEditShortcuts) => {
        const newShortcuts = deepClone(inEditShortcuts);
        const inEditShortcut =
          getShortcut(originalShortcut?.id, newShortcuts) || {};

        Object.assign(inEditShortcut, localShortcut);
        return newShortcuts;
      });
    } else {
      setInEditShortcuts((inEditShortcuts) => {
        return [...inEditShortcuts, { ...localShortcut, id: nanoid() }];
      });
    }
    closeEditForm();
    setIsListDirty(true);
  };

  const discardChanges = () => {
    setLocalShortcut(deepClone(originalShortcut));
    closeEditForm();
  };

  const editFormClasses = classNames(c.editForm, {
    [c.disabled]: isConfirmingBackButton,
  });

  return (
    <>
      <div className={editFormClasses}>
        <div className={c.formFields}>
          <div className={c.field}>
            <div className={c.label}>Preview</div>
            <Shortcut shortcut={localShortcut} hasPlaceholderIcon disabled />
          </div>

          <div className={c.field}>
            <label className={c.label}>Name (required)</label>
            <TextField
              value={localShortcut?.name}
              type="text"
              placeholder="Shortcut"
              error={isNameValid !== null ? () => !isNameValid : null}
              onChange={(e) => updateLocalShortcut(e.target.value, 'name')}
              onBlur={() => {
                setIsNameValid(localShortcut?.name);
              }}
              errorMsg="Name is required"
            />
          </div>

          <div className={c.field}>
            <label className={c.label}>URL (required)</label>
            <TextField
              value={localShortcut?.url}
              type="url"
              placeholder="https://monash.edu"
              error={isUrlValid !== null ? () => !isUrlValid : null}
              onChange={(e) => updateLocalShortcut(e.target.value, 'url')}
              onBlur={() => {
                setIsUrlValid(localShortcut?.url);
              }}
              onPaste={(e) => {
                e.preventDefault();
                const value = e.clipboardData.getData('Text');
                setIsUrlValid(value);
                updateLocalShortcut(value, 'url');
              }}
              errorMsg="URL is required"
            />
          </div>

          <div className={c.field}>
            <Tabs
              tabs={TAB_NAMES}
              selected={selectedTab}
              onChange={(i) => {
                setSelectedTab(i);
              }}
            />

            {selectedTab === 0 && (
              <div className={c.iconsWrapper}>
                <ShortcutIconButton
                  iconData={{
                    type: 'favicon',
                    app: 'Monash Logo',
                  }}
                  inputUrl={localShortcut?.url}
                  selectedIcon={localShortcut?.icon}
                  onClick={(icon) => updateLocalShortcut(icon, 'icon')}
                />
              </div>
            )}

            {selectedTab === 1 && (
              <div className={c.iconsWrapper}>
                {emojis.map((emoji) => (
                  <ShortcutIconButton
                    iconData={{
                      type: 'emoji',
                      character: emoji.character,
                    }}
                    inputUrl={localShortcut?.url}
                    selectedIcon={localShortcut?.icon}
                    onClick={(icon) => updateLocalShortcut(icon, 'icon')}
                    key={emoji.character}
                  />
                ))}
              </div>
            )}

            {selectedTab === 2 && (
              <div className={c.iconsWrapper}>
                {Object.keys(AppIcons).map((app) => (
                  <ShortcutIconButton
                    iconData={{
                      type: 'appIcon',
                      app,
                    }}
                    inputUrl={localShortcut?.url}
                    selectedIcon={localShortcut?.icon}
                    onClick={(icon) => updateLocalShortcut(icon, 'icon')}
                    key={app.app}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isConfirmingBackButton ? (
        <div className={c.actions}>
          <Alert type="error">You have made some changes.</Alert>
          <div className={c.buttons}>
            <Anchor onClick={discardChanges}>Discard</Anchor>
            <Button
              variant="primary"
              size="small"
              onClick={applyChanges}
              mode="card"
              disabled={!isFormValid}
            >
              Keep
            </Button>
          </div>
        </div>
      ) : (
        <div className={c.actions}>
          <div className={c.buttons}>
            <span />

            <Button
              variant="primary"
              size="small"
              onClick={applyChanges}
              mode="card"
              disabled={!isFormValid}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
